import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import '@/App.css';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import IntroVideo from '@/components/IntroVideo';
import Login from '@/pages/Login';
import StudentDashboard from '@/pages/student/Dashboard';
import StudentCourses from '@/pages/student/MyCourses';
import StudentCourseView from '@/pages/student/CourseView';
import StudentLeaderboard from '@/pages/student/Leaderboard';
import StudentProfile from '@/pages/student/Profile';
import StudentAnnouncements from '@/pages/student/Announcements';
import StudentCommunity from '@/pages/student/Community';
import StudentCommunityDetail from '@/pages/student/CommunityDetail';
import StudentLiveClasses from '@/pages/student/LiveClasses';
import StudentHelp from '@/pages/student/Help';
import StudentRewards from '@/pages/student/Rewards';
import Challenges from '@/pages/student/Challenges';
import ChallengeManagement from '@/pages/admin/ChallengeManagement';
import StudentReport from '@/pages/student/Report';
import TeacherDashboard from '@/pages/teacher/Dashboard';
import TeacherCourses from '@/pages/teacher/MyCourses';
import CourseBuilder from '@/pages/teacher/CourseBuilder';
import TeacherLiveClasses from '@/pages/teacher/LiveClasses';
import TeacherAnnouncements from '@/pages/teacher/Announcements';
import TeacherCommunityManager from '@/pages/teacher/CommunityManager';
import TeacherDoubtsResolution from '@/pages/teacher/DoubtsResolution';
import TeacherReports from '@/pages/teacher/TeacherReports';
import TeacherProfile from '@/pages/teacher/TeacherProfile';
import AdminDashboard from '@/pages/admin/EnhancedDashboard';
import AdminUsers from '@/pages/admin/Users';
import AdminAnnouncements from '@/pages/admin/Announcements';
import AdminActivityLog from '@/pages/admin/ActivityLog';
import AdminProfile from '@/pages/admin/AdminProfile';
import AdminCommunityManager from '@/pages/admin/CommunityManager';
import MentorManagement from '@/pages/admin/MentorManagement';
import MentorAssignments from '@/pages/admin/MentorAssignments';
import CustomFieldsManager from '@/pages/admin/CustomFieldsManager';
import DownloadReports from '@/pages/admin/DownloadReports';
import BackupManagement from '@/pages/admin/BackupManagement';
import WebhookManager from '@/pages/admin/WebhookManager';
import PollList from '@/pages/admin/polls/PollList';
import PollDetail from '@/pages/admin/polls/PollDetail';
import StudentMentors from '@/pages/student/Mentors';
import MentorDashboard from '@/pages/mentor/MentorDashboard';
import StudentsList from '@/pages/mentor/StudentsList';
import FollowUpTasks from '@/pages/mentor/FollowUpTasks';
import StudentsNeedAttention from '@/pages/mentor/StudentsNeedAttention';
import RefundManagement from '@/pages/mentor/RefundManagement';
import { Toaster } from '@/components/ui/sonner';

// Separate component that uses AuthContext
function AppContent() {
  const { user, login, logout, loading } = useAuth();
  const [showIntro, setShowIntro] = useState(false);

  useEffect(() => {
    // Check if intro should be shown
    const introShown = sessionStorage.getItem('intro_shown');
    
    // Show intro only if user is not logged in and intro hasn't been shown this session
    if (!user && !loading && !introShown) {
      setShowIntro(true);
    }
  }, [user, loading]);

  const handleLogin = (userData, token) => {
    login(userData, token);
  };

  const handleLogout = () => {
    logout();
  };

  const updateUser = (updatedUserData) => {
    // Update user in both state and localStorage
    login(updatedUserData, localStorage.getItem('lms_token'));
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  // Show intro video before anything else
  if (showIntro) {
    return <IntroVideo onComplete={() => setShowIntro(false)} />;
  }

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={!user ? <Login onLogin={handleLogin} /> : <Navigate to="/" />} />
          
          {/* Student Routes */}
          {user && user.role === 'student' && (
            <>
              <Route path="/" element={<StudentDashboard user={user} onLogout={handleLogout} onUpdateUser={updateUser} />} />
              <Route path="/courses" element={<StudentCourses user={user} onLogout={handleLogout} />} />
              <Route path="/courses/:courseId" element={<StudentCourseView user={user} onLogout={handleLogout} />} />
              <Route path="/leaderboard" element={<StudentLeaderboard user={user} onLogout={handleLogout} />} />
              <Route path="/profile" element={<StudentProfile user={user} onLogout={handleLogout} onUpdateUser={updateUser} />} />
              <Route path="/announcements" element={<StudentAnnouncements user={user} onLogout={handleLogout} />} />
              <Route path="/community" element={<StudentCommunity user={user} onLogout={handleLogout} />} />
              <Route path="/community/:communityId" element={<StudentCommunityDetail user={user} onLogout={handleLogout} />} />
              <Route path="/live-classes" element={<StudentLiveClasses user={user} onLogout={handleLogout} />} />
              <Route path="/help" element={<StudentMentors user={user} onLogout={handleLogout} />} />
              <Route path="/mentors" element={<StudentMentors user={user} onLogout={handleLogout} />} />
              <Route path="/challenges" element={<Challenges user={user} onLogout={handleLogout} />} />
              <Route path="/rewards" element={<StudentRewards user={user} onLogout={handleLogout} />} />
              <Route path="/reports" element={<StudentReport user={user} onLogout={handleLogout} />} />
            </>
          )}

          {/* Mentor Routes */}
          {user && user.role === 'mentor' && (
            <>
              <Route path="/" element={<MentorDashboard user={user} onLogout={handleLogout} />} />
              <Route path="/students" element={<StudentsList user={user} onLogout={handleLogout} />} />
              <Route path="/follow-ups" element={<FollowUpTasks user={user} onLogout={handleLogout} />} />
              <Route path="/attention" element={<StudentsNeedAttention user={user} onLogout={handleLogout} />} />
              <Route path="/refunds" element={<RefundManagement user={user} onLogout={handleLogout} />} />
            </>
          )}

          {/* Teacher Routes */}
          {user && user.role === 'teacher' && (
            <>
              <Route path="/" element={<TeacherDashboard user={user} onLogout={handleLogout} />} />
              <Route path="/courses" element={<TeacherCourses user={user} onLogout={handleLogout} />} />
              <Route path="/courses/:courseId/builder" element={<CourseBuilder user={user} onLogout={handleLogout} />} />
              <Route path="/mentors" element={<MentorManagement user={user} onLogout={handleLogout} />} />
              <Route path="/live-classes" element={<TeacherLiveClasses user={user} onLogout={handleLogout} />} />
              <Route path="/announcements" element={<TeacherAnnouncements user={user} onLogout={handleLogout} />} />
              <Route path="/community" element={<TeacherCommunityManager user={user} onLogout={handleLogout} />} />
              <Route path="/community/:communityId" element={<StudentCommunityDetail user={user} onLogout={handleLogout} />} />
              <Route path="/doubts-resolution" element={<TeacherDoubtsResolution user={user} onLogout={handleLogout} />} />
              <Route path="/teacher-reports" element={<TeacherReports user={user} onLogout={handleLogout} />} />
              <Route path="/teacher-profile" element={<TeacherProfile user={user} onLogout={handleLogout} onUpdateUser={updateUser} />} />
              <Route path="/challenge-management" element={<ChallengeManagement user={user} onLogout={handleLogout} />} />
            </>
          )}

          {/* Admin Routes */}
          {user && user.role === 'admin' && (
            <>
              <Route path="/" element={<AdminDashboard user={user} onLogout={handleLogout} />} />
              <Route path="/courses" element={<TeacherCourses user={user} onLogout={handleLogout} />} />
              <Route path="/courses/:courseId/builder" element={<CourseBuilder user={user} onLogout={handleLogout} />} />
              <Route path="/users" element={<AdminUsers user={user} onLogout={handleLogout} />} />
              <Route path="/mentors" element={<MentorManagement user={user} onLogout={handleLogout} />} />
              <Route path="/mentor-assignments" element={<MentorAssignments user={user} onLogout={handleLogout} />} />
              <Route path="/live-classes" element={<TeacherLiveClasses user={user} onLogout={handleLogout} />} />
              <Route path="/announcements" element={<AdminAnnouncements user={user} onLogout={handleLogout} />} />
              <Route path="/community" element={<AdminCommunityManager user={user} onLogout={handleLogout} />} />
              <Route path="/community/:communityId" element={<StudentCommunityDetail user={user} onLogout={handleLogout} />} />
              <Route path="/doubts-resolution" element={<TeacherDoubtsResolution user={user} onLogout={handleLogout} />} />
              <Route path="/activity-log" element={<AdminActivityLog user={user} onLogout={handleLogout} />} />
              <Route path="/admin-profile" element={<AdminProfile user={user} onLogout={handleLogout} onUpdateUser={updateUser} />} />
              <Route path="/challenge-management" element={<ChallengeManagement user={user} onLogout={handleLogout} />} />
              <Route path="/custom-fields" element={<CustomFieldsManager user={user} onLogout={handleLogout} />} />
              <Route path="/download-reports" element={<DownloadReports user={user} onLogout={handleLogout} />} />
              <Route path="/backup-management" element={<BackupManagement user={user} onLogout={handleLogout} />} />
              <Route path="/webhook-manager" element={<WebhookManager user={user} onLogout={handleLogout} />} />
              <Route path="/admin/polls" element={<PollList user={user} onLogout={handleLogout} />} />
              <Route path="/admin/polls/:pollId" element={<PollDetail user={user} onLogout={handleLogout} />} />
            </>
          )}

          {/* Default redirect for root path */}
          {user && user.role === 'mentor' && (
            <Route path="/" element={<Navigate to="/mentor/dashboard" replace />} />
          )}

          <Route path="*" element={<Navigate to={user ? "/" : "/login"} />} />
        </Routes>
      </BrowserRouter>
      <Toaster />
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;